import { useEffect, useMemo, useState } from 'react';
import { MdDownload, MdRefresh, MdVpnKey, MdClose } from 'react-icons/md';
import { useUser } from '../context/UserContext';
import { consultarSeguimientoCesionesFacturacion } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';
import {
  defaultBusinessDaysWindow,
  fechaCorteAnalisisISO,
  maxBusinessDaysInMonth,
} from '../utils/feriadosChile';
import {
  aggregateDailySeries,
  buildAvancePorMes,
  buildMesesCerradosCompletos,
  buildVariacionUltimosMeses,
  closedMonthsThrough,
  monthsInRollingWindow,
  normalizeEquipo,
  resolveWindowN,
  rollingWindowStartISO,
  ROLLING_WINDOW_MONTHS,
  SIN_EQUIPO,
  SIN_EJECUTIVO,
  uniqueDimensionLabels,
} from '../utils/seguimientoCesionesFacturacion';
import { downloadSeguimientoCesionesExcel } from '../utils/exportSeguimientoCesionesExcel';

const formatMonto = (v) =>
  formatNumberEsCL(Number(v) || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatPct = (v, { asRatio = false } = {}) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = asRatio ? Number(v) * 100 : Number(v);
  return `${formatNumberEsCL(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const formatVarPct = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v) * 100;
  const abs = formatNumberEsCL(Math.abs(n), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (n > 0) return `+${abs}%`;
  if (n < 0) return `-${abs}%`;
  return '0,0%';
};

const toggleInList = (arr, value) =>
  arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

const VISTA_AVANCE = 'avance';
const VISTA_CERRADOS = 'cerrados';

const TablaComparacionMeses = ({ rows, variacion, loading, emptyMessage }) => (
  <div className="tabla-container overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-slate-100 text-left">
          <th className="px-3 py-2 font-semibold text-gray-700">Mes</th>
          <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">Desde</th>
          <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">Hasta</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones IF</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones Otros</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Participación IF</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Total facturación</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Total cesiones</th>
          <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones / Fact.</th>
        </tr>
      </thead>
      <tbody>
        {loading && !rows.length ? (
          <tr>
            <td colSpan={9} className="px-3 py-4 text-gray-500">
              Cargando…
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={9} className="px-3 py-4 text-gray-500">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.label} className="border-t border-gray-200 hover:bg-slate-50">
              <td className="px-3 py-2 whitespace-nowrap font-medium">{r.label}</td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.desde}</td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.hasta}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cedIf)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cedOtro)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatPct(r.participacionIf, { asRatio: true })}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.facturacion)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cesion)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatPct(r.cesionesSobreFacturacion, { asRatio: true })}
              </td>
            </tr>
          ))
        )}
        {variacion && (
          <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
            <td className="px-3 py-2" colSpan={3}>
              {variacion.label}
            </td>
            <td className="px-3 py-2 text-right">{formatVarPct(variacion.cedIf)}</td>
            <td className="px-3 py-2 text-right">{formatVarPct(variacion.cedOtro)}</td>
            <td className="px-3 py-2 text-right">—</td>
            <td className="px-3 py-2 text-right">{formatVarPct(variacion.facturacion)}</td>
            <td className="px-3 py-2 text-right">{formatVarPct(variacion.cesion)}</td>
            <td className="px-3 py-2 text-right">—</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const parseApiPayload = (resp) => {
  let data = resp;
  if (data?.body && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      /* keep */
    }
  }
  return data;
};

const SeguimientoCesionesFacturacion = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filasRaw, setFilasRaw] = useState([]);
  const [distClaves, setDistClaves] = useState(null);
  const [fechaCorte, setFechaCorte] = useState(() => fechaCorteAnalisisISO());
  const [ventanaDesde, setVentanaDesde] = useState(() => rollingWindowStartISO(fechaCorteAnalisisISO()));
  const [nInput, setNInput] = useState('');
  const [equiposSel, setEquiposSel] = useState([]);
  const [ejecutivosSel, setEjecutivosSel] = useState([]);
  const [showDiario, setShowDiario] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showClavesModal, setShowClavesModal] = useState(false);
  const [vistaComparacion, setVistaComparacion] = useState(VISTA_AVANCE);

  const maxN = useMemo(() => maxBusinessDaysInMonth(fechaCorte), [fechaCorte]);
  const defaultN = useMemo(() => defaultBusinessDaysWindow(fechaCorte), [fechaCorte]);
  const nHabiles = useMemo(() => resolveWindowN(fechaCorte, nInput || defaultN), [fechaCorte, nInput, defaultN]);

  const equiposOpts = useMemo(
    () => uniqueDimensionLabels(filasRaw.map((r) => r.EQUIPO_VENTAS), { emptyLabel: SIN_EQUIPO }),
    [filasRaw],
  );

  /** Ejecutivos visibles: si hay equipo(s) seleccionado(s), solo los de esos equipos. */
  const ejecutivosOpts = useMemo(() => {
    const equiposSet = new Set(equiposSel);
    const fuente = equiposSet.size
      ? filasRaw.filter((r) => equiposSet.has(normalizeEquipo(r.EQUIPO_VENTAS)))
      : filasRaw;
    return uniqueDimensionLabels(
      fuente.map((r) => r.EJECUTIVO),
      { emptyLabel: SIN_EJECUTIVO },
    );
  }, [filasRaw, equiposSel]);

  // Al cambiar equipos, quitar ejecutivos que ya no pertenecen a la selección.
  useEffect(() => {
    setEjecutivosSel((prev) => {
      if (!prev.length) return prev;
      const allowed = new Set(ejecutivosOpts);
      const next = prev.filter((ej) => allowed.has(ej));
      return next.length === prev.length ? prev : next;
    });
  }, [ejecutivosOpts]);

  const daily = useMemo(
    () =>
      aggregateDailySeries(filasRaw, {
        equipos: equiposSel,
        ejecutivos: ejecutivosSel,
      }),
    [filasRaw, equiposSel, ejecutivosSel],
  );

  const avance = useMemo(
    () => buildAvancePorMes(daily, fechaCorte, nHabiles),
    [daily, fechaCorte, nHabiles],
  );
  const mesesCerrados = useMemo(
    () => buildMesesCerradosCompletos(daily, fechaCorte),
    [daily, fechaCorte],
  );
  const mesesVentana = useMemo(() => monthsInRollingWindow(fechaCorte), [fechaCorte]);
  const mesesCerradosMeta = useMemo(() => closedMonthsThrough(fechaCorte), [fechaCorte]);
  const filasComparacion = vistaComparacion === VISTA_CERRADOS ? mesesCerrados : avance;
  const variacion = useMemo(
    () => buildVariacionUltimosMeses(filasComparacion),
    [filasComparacion],
  );

  const claveOk = Number(distClaves?.clave_ok) || 0;
  const claveErr = Number(distClaves?.clave_erronea) || 0;
  const claveTotal = Number(distClaves?.total) || claveOk + claveErr;
  const pctClaveOk = claveTotal > 0 ? (claveOk / claveTotal) * 100 : 0;
  const pctClaveErr = claveTotal > 0 ? (claveErr / claveTotal) * 100 : 0;

  const cargar = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await consultarSeguimientoCesionesFacturacion(user);
      const data = parseApiPayload(resp);
      if (data?.error || data?.ok === false) {
        throw new Error(data.message || data.error || 'Error al consultar');
      }
      const filas = Array.isArray(data?.filas) ? data.filas : Array.isArray(data) ? data : [];
      const corte = data?.fecha_corte || fechaCorteAnalisisISO();
      setFilasRaw(filas);
      setDistClaves(data?.distribucion_claves || null);
      setFechaCorte(corte);
      setVentanaDesde(data?.ventana_desde || rollingWindowStartISO(corte));
      setNInput(String(defaultBusinessDaysWindow(corte)));
      setEquiposSel([]);
      setEjecutivosSel([]);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo cargar el seguimiento.');
      setFilasRaw([]);
      setDistClaves(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial
  }, []);

  const exportarExcel = async () => {
    if (!daily.length && !avance.length) return;
    setExporting(true);
    try {
      await downloadSeguimientoCesionesExcel({
        daily,
        fechaCorte,
        nHabiles,
        avance,
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo exportar el Excel.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Seguimiento cesiones / facturación</h1>
          <p className="text-sm text-gray-600 mt-1">
            Ventana móvil <strong>{ROLLING_WINDOW_MONTHS} meses</strong>
            {ventanaDesde && fechaCorte ? (
              <>
                {' '}
                (<strong>{ventanaDesde}</strong> → <strong>{fechaCorte}</strong>)
              </>
            ) : null}
            {vistaComparacion === VISTA_AVANCE ? (
              <>
                {' '}
                · Ventana: <strong>{nHabiles}</strong> día{nHabiles === 1 ? '' : 's'} hábil
                {nHabiles === 1 ? '' : 'es'}
              </>
            ) : (
              <>
                {' '}
                · <strong>{mesesVentana.length}</strong> meses en comparación · cerrados hasta{' '}
                <strong>
                  {mesesCerradosMeta.lastClosedMonth > 0
                    ? `${mesesCerradosMeta.lastClosedMonth}/${mesesCerradosMeta.year}`
                    : '—'}
                </strong>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
            disabled={loading || !distClaves}
            onClick={() => setShowClavesModal(true)}
            title={!distClaves ? 'Sin datos de claves (¿Lambda actualizada?)' : undefined}
          >
            <MdVpnKey className="w-4 h-4" />
            Distribución de claves
          </button>
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
            disabled={loading}
            onClick={cargar}
          >
            <MdRefresh className="w-4 h-4" />
            {loading ? 'Cargando…' : 'Actualizar'}
          </button>
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
            disabled={!daily.length || exporting}
            onClick={exportarExcel}
          >
            <MdDownload className="w-4 h-4" />
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      <div className="seccion-datos mb-6 space-y-4">
        {vistaComparacion === VISTA_AVANCE && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Días hábiles a comparar (N)
              </label>
              <input
                type="number"
                min={1}
                max={maxN}
                value={nInput}
                onChange={(e) => setNInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default mes actual: {defaultN}. Máx. del mes: {maxN}.
              </p>
            </div>
            <div className="md:col-span-2 text-sm text-gray-600">
              Se suman los primeros N días hábiles chilenos de cada mes (incluye montos de
              fines de semana/feriados hasta esa fecha). Sin filtros = totales globales.
            </div>
          </div>
        )}

        {vistaComparacion === VISTA_CERRADOS && (
          <p className="text-sm text-gray-600">
            Cada fila es el mes calendario completo (del 1 al último día). Solo se listan meses
            cerrados dentro de la ventana móvil de {ROLLING_WINDOW_MONTHS} meses; el mes en curso
            aparece cuando el corte cae en su último día.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Equipo ventas</label>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setEquiposSel([])}
              >
                Todos
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
              {equiposOpts.length === 0 ? (
                <p className="text-xs text-gray-400 px-1">Sin datos</p>
              ) : (
                equiposOpts.map((eq) => (
                  <label key={eq} className="flex items-center gap-2 text-sm text-gray-700 px-1">
                    <input
                      type="checkbox"
                      checked={equiposSel.includes(eq)}
                      onChange={() => setEquiposSel((prev) => toggleInList(prev, eq))}
                    />
                    <span>{eq}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Ejecutivo</label>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setEjecutivosSel([])}
              >
                Todos
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
              {ejecutivosOpts.length === 0 ? (
                <p className="text-xs text-gray-400 px-1">Sin datos</p>
              ) : (
                ejecutivosOpts.map((ej) => (
                  <label key={ej} className="flex items-center gap-2 text-sm text-gray-700 px-1">
                    <input
                      type="checkbox"
                      checked={ejecutivosSel.includes(ej)}
                      onChange={() => setEjecutivosSel((prev) => toggleInList(prev, ej))}
                    />
                    <span>{ej}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
        )}
      </div>

      <div className="seccion-datos mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4 border-b border-gray-200">
          <button
            type="button"
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              vistaComparacion === VISTA_AVANCE
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            onClick={() => setVistaComparacion(VISTA_AVANCE)}
          >
            Avance N días hábiles
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              vistaComparacion === VISTA_CERRADOS
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            onClick={() => setVistaComparacion(VISTA_CERRADOS)}
          >
            Meses cerrados completos
          </button>
        </div>

        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {vistaComparacion === VISTA_CERRADOS
            ? 'Comparación meses cerrados'
            : `Avance ${nHabiles} día${nHabiles === 1 ? '' : 's'} hábil${nHabiles === 1 ? '' : 'es'} de gestión`}
        </h2>

        <TablaComparacionMeses
          rows={filasComparacion}
          variacion={variacion}
          loading={loading}
          emptyMessage={
            vistaComparacion === VISTA_CERRADOS
              ? `Aún no hay meses cerrados en la ventana de ${ROLLING_WINDOW_MONTHS} meses.`
              : 'Sin datos para el corte y filtros actuales.'
          }
        />
      </div>

      <div className="seccion-datos">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">
            Serie diaria ({daily.length.toLocaleString('es-CL')} días)
          </h2>
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => setShowDiario((v) => !v)}
          >
            {showDiario ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showDiario && (
          <div className="tabla-container overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-slate-100">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-gray-700">Día</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones IF</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones Otros</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Part. IF</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Facturación</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Cesiones</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Ces. / Fact.</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((r) => (
                  <tr key={r.fecha} className="border-t border-gray-200 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{r.fecha}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cedIf)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cedOtro)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatPct(r.participacionIf, { asRatio: true })}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatMonto(r.facturacion)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatMonto(r.cesion)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatPct(r.cesionesSobreFacturacion, { asRatio: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showClavesModal && distClaves && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowClavesModal(false);
          }}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-lg bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dist-claves-title"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h2 id="dist-claves-title" className="text-lg font-semibold text-gray-800">
                Distribución de claves
              </h2>
              <button
                type="button"
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                onClick={() => setShowClavesModal(false)}
                aria-label="Cerrar"
              >
                <MdClose className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                    Clave OK (200)
                  </div>
                  <div className="mt-1 text-xl font-bold text-emerald-900 tabular-nums">
                    {formatMonto(claveOk)}
                  </div>
                  <div className="text-sm text-emerald-700">{formatPct(pctClaveOk)} del total</div>
                </div>
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-red-700">
                    Clave errónea (500)
                  </div>
                  <div className="mt-1 text-xl font-bold text-red-900 tabular-nums">
                    {formatMonto(claveErr)}
                  </div>
                  <div className="text-sm text-red-700">{formatPct(pctClaveErr)} del total</div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-600">
                    Total RUTs
                  </div>
                  <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">
                    {formatMonto(claveTotal)}
                  </div>
                  <div className="text-sm text-slate-600">lcv_estado_clave</div>
                </div>
              </div>
              {claveTotal > 0 && (
                <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200 flex">
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${pctClaveOk}%` }}
                    title={`Clave OK ${formatPct(pctClaveOk)}`}
                  />
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${pctClaveErr}%` }}
                    title={`Clave errónea ${formatPct(pctClaveErr)}`}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end border-t border-gray-200 px-5 py-3">
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => setShowClavesModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeguimientoCesionesFacturacion;
